import React, { useMemo } from 'react';
import { NavBar, NavBarProps } from '../../../NavBar';
import { Category, EventInfoFragmentFragment, EventPageFragment, EventPageType } from '@graphql/generated';
import { StyledRoot, StyledTitle, styles } from './EventNameNav.styles';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { Link } from '@react-router';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core/i18n';
import { isInIframe } from '@shared/utils/isInIframe';
import { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { animationTransition } from '@shared/utils/animationTransition';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { InlineEditor } from '@shared/components';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { Edit } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { useColorPaletteProvider } from '@apps/guest/packages/layout-engine/layouts/ColorPaletteProvider/ColorPaletteProvider';
import { getButtonPropsBasedOnBackground } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { CustomArtMobile } from '../../../CustomArt/CustomArt';
export interface EventNameNavProps {
  eventHandle: string;
  info: EventInfoFragmentFragment;
  pages: ReadonlyArray<EventPageFragment>;
  navBarVariant: NavBarProps['variant'];
  applicatorProp: object;
  handleMenuClicked?: (item: string) => void;
  handleEditEventClicked?: () => void;
  eventProps: NavbarEventPropsInterface;
  page: Maybe<EventPageType>;
  pageSlug?: string;
}

interface Props extends EventNameNavProps {
  eventDisplayName: Maybe<string>;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE },
];

/**
 * This is a straight clone of the Header.tsx component
 */
const EventNameNav: React.FC<Props> = ({
  applicatorProp,
  eventHandle,
  info,
  eventDisplayName,
  pages,
  navBarVariant,
  handleMenuClicked,
  handleEditEventClicked,
  eventProps,
  page,
  pageSlug,
  children,
  ...restProps
}) => {
  const { isAdmin } = useEventUserRole();
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isXsScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'xs' }));
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pageColor, pageFontColor, artBackgroundFontColor } = useColorPaletteProvider();

  const { t2 } = useTranslation('eventMenu');
  const editEventText = t2('editEvent');
  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');
  const customEditEventButtonProps = useMemo(() => (pageColor ? { ...getButtonPropsBasedOnBackground(pageColor), startIcon: <Edit size={16} /> } : {}), [pageColor]);
  const pageFontColorProps = useMemo(() => (pageFontColor ? { color: pageFontColor } : {}), [pageFontColor]);

  const isPreviewing = isInIframe();
  // The xs breakpoint brannan layout 375 when in Iframe and 480 when not in Iframe.
  // This causes difference of height between preview and guest
  const eventNameMinHeight = useMemo(() => [isPreviewing ? 291 : 200, 200, null, 'initial'], [isPreviewing]);

  const handleEditTextClick = useEventCallback(() => {
    // send the message to the parent window to open the text editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editText',
        inlineEditData: { textContent: eventDisplayName || '', label: inlineEditorTransFn.displayName(), validationConstraints: { maxLength: 30 } },
      },
    });
  });

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } },
    });
  });

  return (
    <>
      <StyledRoot
        {...restProps}
        position={'relative'}
        {...applicatorProp}
        data-uh="nah"
      >
        {isMobileOrTablet && eventProps.isCustomArtEnabled && <CustomArtMobile backgroundImageUrl={eventProps.customArtUrl} />}
        <Flex
          width="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={eventNameMinHeight}
        >
          <Box
            height={{ _: 'auto', sm2: 'auto' }}
            transition={animationTransition('height')}
            width="100%"
            {...pageFontColorProps}
          >
            <StyledTitle
              __css={styles.title}
              data-testid="header-title"
              {...(isMobile && { color: artBackgroundFontColor })}
            >
              <InlineEditor
                elementLabel={inlineEditorTransFn.displayName()}
                actionData={{
                  edit: handleEditTextClick,
                  font: () => handleEditFontClick(Category.EVENT_TITLE),
                }}
                wrapperCSS={{
                  zIndex: 'banner',
                }}
                inset={isMobile ? -12 : -16}
                componentName="eventDisplayName"
                pageName={page}
                pageSlug={pageSlug || ''}
              >
                <GuestSiteTypographyOverride override={typographyOverride}>
                  <TextV2
                    // The LCP element, large block-level text
                    as="h1"
                    typographyVariant={isXsScreen ? 'display4' : 'display6'}
                  >
                    {eventDisplayName}
                  </TextV2>
                </GuestSiteTypographyOverride>
              </InlineEditor>
            </StyledTitle>
            {isAdmin && !isPreviewing && navBarVariant === 'inline' && (
              <ButtonV2
                onClick={handleEditEventClicked}
                intent="primary"
                size="sm"
                typographyVariant="button1"
                variant="outline"
                position="absolute"
                top="19px"
                right="16px"
                as={Link}
                to={`/${eventHandle}/edit/dashboard`}
                {...customEditEventButtonProps}
              >
                {editEventText}
              </ButtonV2>
            )}
            <NavBar
              handleMenuClicked={handleMenuClicked}
              title={eventDisplayName}
              variant={navBarVariant}
              data-testid={'header-nav'}
              eventHandle={eventHandle}
              pages={pages}
              eventProps={eventProps}
            />
            {!isMobileOrTablet && children}
          </Box>
        </Flex>
      </StyledRoot>

      {/* children will typically be the header photo, which should be placed below the component title banner on mobile/tablet*/}
      {isMobileOrTablet && children}
    </>
  );
};

EventNameNav.displayName = 'Header';

export { EventNameNav };
